/**
 * Departamentos a los que se les puede pedir algo desde el modulo Solicitudes.
 *
 * El modulo es la puerta de entrada: el usuario elige a que departamento le
 * pide y desde ahi entra al flujo de ese departamento: RRHH (migrado de
 * Flokzu), Gestion de Talento (solicitudes de capacitacion), SST y GafCORE
 * (traslados de activos y mantenimiento correctivo, los atiende GAF).
 *
 * Para agregar un departamento:
 *
 *   1. Sumar una entrada aca, con su `moduloPermiso` (el id que usa
 *      modulePermissions para decidir quien lo ve) y su `ruta`.
 *   2. Registrar el modulo en MODULE_DEFINITIONS y su default por cargo en
 *      config/modulePermissions.ts, para que se pueda gatear por rol.
 *   3. Agregar las rutas de sus pantallas en App.tsx.
 *
 * Si el departamento nuevo tambien publica un catalogo declarativo como el de
 * RRHH (motivos, campos y condicionales servidos por EMSYS), las pantallas de
 * carga y detalle se pueden reusar tal cual, porque no tienen nada de RRHH
 * hardcodeado: se arman con lo que declara el catalogo. En ese caso lo unico
 * propio del departamento son los endpoints.
 */
import { businessOutline, peopleOutline, schoolOutline, shieldCheckmarkOutline, swapHorizontalOutline } from "ionicons/icons";

import {
  canViewModule,
  ModulePermissionsMap,
  normalizeCargoKey,
} from "src/config/modulePermissions";
import { cargarPermisoCapacitacion, permisoCapacitacionEnSesion } from "src/services/solicitudCapacitacion";
import { cargarPermisoRrhh, permisoRrhhEnSesion } from "src/services/solicitudesRRHH";
import { cargarPermisoSst, permisoSstEnSesion } from "src/services/sst";

export interface DepartamentoSolicitud {
  /** Identificador estable, para logs y analitica. */
  id: string;
  /** Nombre del departamento, como lo ve el usuario. */
  titulo: string;
  /** Que se le pide a este departamento. Se muestra debajo del titulo. */
  descripcion: string;
  icono: string;
  /** A donde entra el usuario al elegirlo. */
  ruta: string;
  /**
   * Id del modulo en modulePermissions. Decide quien ve el departamento en la
   * lista; el permiso real lo revalida EMSYS igual.
   */
  moduloPermiso: string;
  /**
   * Si lo ve un cargo que no matchea ningun rol de modulePermissions (la
   * mayoria de los administrativos: "CONTADOR", "ANALISTA DE ...").
   * true solo si el backend del departamento acepta administrativos.
   */
  abiertoACargosSinRol: boolean;
  /**
   * Respuesta de EMSYS cacheada en la sesion (true / false; null = todavia no
   * se sabe). Para los departamentos que aceptan administrativos por el tipo de
   * funcionario del DWH, que la app no conoce: el cargo solo no alcanza.
   */
  permisoEnSesion?: () => boolean | null;
  /** Pide el permiso a EMSYS (y lo deja en la sesion). Va junto con permisoEnSesion. */
  cargarPermiso?: () => Promise<boolean | null>;
}

/**
 * Si el usuario ve el departamento en el hub de Solicitudes.
 *
 * Sin respuesta de EMSYS manda el permiso por cargo. Con respuesta, manda
 * EMSYS: un "no" lo oculta, y un "si" lo muestra aunque el rol por defecto no
 * lo tenga (p. ej. Gerente General Adjunto, administrativo), salvo que el
 * cargo este apagado a mano en Permisos de Modulos.
 */
export const departamentoVisible = (
  departamento: DepartamentoSolicitud,
  cargo: string | undefined,
  permisos: ModulePermissionsMap,
): boolean => {
  const porCargo = canViewModule(departamento.moduloPermiso, cargo, permisos, {
    cargoSinRol: departamento.abiertoACargosSinRol,
  });
  const emsys = departamento.permisoEnSesion?.() ?? null;
  if (emsys === null) return porCargo;
  if (!emsys) return false;
  const apagadoAMano =
    permisos[departamento.moduloPermiso]?.[normalizeCargoKey(cargo)] === false;
  return !apagadoAMano;
};

export const DEPARTAMENTOS_SOLICITUDES: DepartamentoSolicitud[] = [
  {
    id: "rrhh",
    titulo: "Recursos Humanos",
    descripcion:
      "Ausencias, suspensiones, desvinculaciones, contratos, " +
      "apercibimientos y descuentos",
    icono: peopleOutline,
    ruta: "/solicitudes-rrhh",
    moduloPermiso: "solicitudes_rrhh",
    // Por cargo solo los roles conocidos; los administrativos los habilita
    // EMSYS (solicitudes-rrhh/permiso) por el tipo de funcionario.
    abiertoACargosSinRol: false,
    permisoEnSesion: permisoRrhhEnSesion,
    cargarPermiso: cargarPermisoRrhh,
  },
  {
    id: "gestion_talento",
    titulo: "Gestión de Talento",
    descripcion: "Capacitaciones técnicas para operarios o puntos de servicio",
    icono: schoolOutline,
    ruta: "/solicitudes-capacitacion",
    moduloPermiso: "solicitudes_capacitacion",
    // Por cargo fiscales, supervisores, jefes de operaciones y capacitadores; los
    // administrativos los habilita api-central (solicitud-capacitacion/rol) por
    // el tipo de funcionario.
    abiertoACargosSinRol: false,
    permisoEnSesion: permisoCapacitacionEnSesion,
    cargarPermiso: cargarPermisoCapacitacion,
  },
  {
    id: "sst",
    titulo: "Seguridad y Salud en el Trabajo",
    descripcion:
      "Acompañamiento técnico, análisis de riesgos, capacitaciones, " +
      "planes de emergencia y evaluaciones",
    icono: shieldCheckmarkOutline,
    ruta: "/sst/solicitudes",
    moduloPermiso: "solicitudes_sst",
    abiertoACargosSinRol: true,
    permisoEnSesion: permisoSstEnSesion,
    cargarPermiso: cargarPermisoSst,
  },
  {
    id: "gafcore",
    titulo: "GafCORE",
    descripcion: "Traslados de equipos y mantenimiento de equipos con fallas",
    icono: swapHorizontalOutline,
    // Ahi se elige Traslados o Mantenimientos (como en EMSYS).
    ruta: "/gafcore",
    moduloPermiso: "solicitudes_gafcore",
    abiertoACargosSinRol: true,
  },
];

/** Icono del modulo, para el menu y el estado vacio de la pantalla. */
export const ICONO_SOLICITUDES = businessOutline;
